import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link } from 'react-router-dom';
import { updateBreadcrumb } from '#action/breadcrumb';
import { fetchMeta } from '#action/meta/device';
import { fetchPaginatedProjects, changePage, clearPaginatedProjects } from '#action/project/projects-paginated';
import PageBar from '#component/general/page-bar';
import FilterWrapper from '#component/general/list-filter/filter-wrapper';
import LargeFixedButton from '#component/general/large-fixed-button';
import PreloaderComponent from '#component/preloader';
import { useListFilterDataLoading } from '#component/general/list-filter/hooks';
import filterConfig from './filter/config';
import { getSearchParams } from './helper';
import CardView from './cards';
import TableView from './table/table-view';
import {
  Container,
  AddButtonArea,
  AddButtonLabel,
  MobileCardContainer,
  MobilePaginationContainer,
  StyledPagination,
  PaginationPlaceholder
} from './styles';
import { useUrlPagination } from '#hooks';
import { setDocumentTitle } from '#helper';

/**
 * Renders projects dashboard.
 * @returns {React.FC}
 */

const ProjectDashboard = props => {
  const { isMobile, history } = props;
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobileMediaQuery = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.XGA));
  const dispatch = useDispatch();
  const account = useSelector(s => s.account, shallowEqual);
  const {
    projects,
    page,
    pages,
    status
  } = useSelector(s => s.projectsPaginated, shallowEqual);
  const listFilterStatus = useSelector(s => s.listFilter.status, shallowEqual);
  const isProjectsLoading = status !== 'succeeded' || status === 'idle' || loading || !listFilterStatus;
  const filterData = useMemo(() => filterConfig, []);

  useUrlPagination({ page, pages, changePage });

  useEffect(() => {
    setDocumentTitle('Projects overview');
    dispatch(updateBreadcrumb([{ label: 'Projects' }]));
  });

  const fetchData = useCallback(async () => {
    await dispatch(fetchPaginatedProjects());
    await dispatch(fetchMeta());
    setLoading(false);
  }, [dispatch]);

  const clearData = useCallback(async () => {
    dispatch(clearPaginatedProjects());
  }, [dispatch]);

  useListFilterDataLoading(fetchData, clearData);

  const replaceHistoryParams = useCallback(
    newParams =>
      new Promise(res => {
        const { location: { search } } = history;
        const params = getSearchParams(search, newParams);

        history.replace(`?${params}`);
        res(true);
      }),
    [history]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const handlePageChange = async (event, newPage) => {
    setLoading(true);
    if (page !== newPage) {
      await replaceHistoryParams({ filterType: 'page', value: newPage });
      dispatch(changePage(newPage));
    }
    setLoading(false);
  };

  const reloadProjects = async () => {
    setLoading(true);
    await dispatch(fetchPaginatedProjects());

    setLoading(false);
  };

  const isMobileView = isMobile || isMobileMediaQuery;
  const cardsBottomPadding = !!pages && Number(pages) > 1 ? '0' : '75px';

  return (
    <section>
      <PageBar view="project">
        <FilterWrapper data={filterData} />
        {!isMobileView &&
        <AddButtonArea>
          <Button
            size="small"
            color="secondary"
            aria-label="Add"
            component={Link}
            to="/addproject"
          >
            <AddIcon />
            <AddButtonLabel>Add project</AddButtonLabel>
          </Button>
        </AddButtonArea>}
      </PageBar>
      <Container className="default-table">
        {isMobileView ? loading ? <PreloaderComponent /> : (
          <MobileCardContainer>
            <div style={{ paddingBottom: cardsBottomPadding }}>
              <CardView
                projects={projects}
                account={account}
                loading={isProjectsLoading}
                reloadProjects={reloadProjects}
              />
            </div>
            <LargeFixedButton directTo="/addproject" buttonName="Add project" />
          </MobileCardContainer>
        ) : (
          <TableView
            projects={projects}
            account={account}
            dispatch={dispatch}
            loading={isProjectsLoading}
            replaceHistoryParams={replaceHistoryParams}
          />
        )}
      </Container>
      {!!pages && Number(pages) > 1 && !isProjectsLoading ? (
        <MobilePaginationContainer>
          <StyledPagination
            shape="rounded"
            color="secondary"
            count={pages}
            page={page}
            onChange={handlePageChange}
          />
        </MobilePaginationContainer>
      ) : (
        <PaginationPlaceholder />
      )}
    </section>
  );
};

export default ProjectDashboard;
